import React, { useState, useEffect, useRef } from 'react';
import { questions as questionsCMO } from '../data/questions_cmo';
import { questions as questionsFounder } from '../data/questions_founder';
import saveToDatabase from './ResultProcessor';
import './SurveyStyles.css';
import HumanizingAuditMobileImage from './HumanizingAudit_MobileImage.png';

// Helper hook to detect mobile device
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
}

const SurveyForm = ({ version = 'founder', partner = 'default' }) => {
  // Version and partner are passed as props from the router
  
  const [responses, setResponses] = useState({});
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [isPersonalInfoStage, setIsPersonalInfoStage] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    company: '',
    fundingStage: '',
    fundingStageOther: '',
    jobTitle: '',
    jobTitleOther: '',
    email: '',
  });
  const [showCoFounderQuestions, setShowCoFounderQuestions] = useState(false);
  const [error, setError] = useState('');
  const [showIntro, setShowIntro] = useState(true);

  const questionRefs = useRef([]);
  const nextButtonRef = useRef(null);
  const containerRef = React.useRef(null);

  // For marketing version, all non-founder roles use CMO questions
  // For founder version, use founder questions
  let selectedQuestions =
    version === 'founder' || personalInfo.jobTitle === 'Founder' ? questionsFounder : questionsCMO;

  // Filter out Co-Founder questions if not a Co-Founder or Founder (With a Co-Founder)
  if (version === 'founder' && personalInfo.jobTitle !== 'Co-Founder' && personalInfo.jobTitle !== 'Founder (With a Co-Founder)') {
    selectedQuestions = selectedQuestions.filter(q => q.section !== 'Relationship with your Co-Founder');
  }

  const sectionTitles = [...new Set(selectedQuestions.map(q => q.section))];
  const currentSection = sectionTitles[currentSectionIndex] || '';
  const currentQuestions = selectedQuestions.filter(q => q.section === currentSection);
  const sectionDescription = currentQuestions.find(q => q.description)?.description || '';

  const allCurrentAnswered = currentQuestions.every(
    q => responses[`${currentSection}-${q.id}`] !== undefined
  );

  // Scroll to active question or button
  useEffect(() => {
    const target = questionRefs.current[visibleIndex] || questionRefs.current[0];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [visibleIndex, currentSectionIndex]);

  useEffect(() => {
    if (allCurrentAnswered && nextButtonRef.current) {
      setTimeout(() => {
        nextButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, [allCurrentAnswered]);

  // Load progress from localStorage on mount (partner-specific to prevent conflicts)
  const storageKey = `surveyProgress-${partner}-${version}`;
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const progress = JSON.parse(saved);
        if (progress.responses) setResponses(progress.responses);
        if (progress.personalInfo) setPersonalInfo(progress.personalInfo);
        if (typeof progress.currentSectionIndex === 'number') setCurrentSectionIndex(progress.currentSectionIndex);
        if (typeof progress.visibleIndex === 'number') setVisibleIndex(progress.visibleIndex);
        if (typeof progress.isPersonalInfoStage === 'boolean') setIsPersonalInfoStage(progress.isPersonalInfoStage);
        if (typeof progress.showIntro === 'boolean') setShowIntro(progress.showIntro);
      } catch (e) {
        // Ignore parse errors
      }
    }
  }, [storageKey]);

  // Save progress to localStorage on change (partner-specific)
  useEffect(() => {
    const progress = {
      responses,
      personalInfo,
      currentSectionIndex,
      visibleIndex,
      isPersonalInfoStage,
      showIntro
    };
    localStorage.setItem(storageKey, JSON.stringify(progress));
  }, [responses, personalInfo, currentSectionIndex, visibleIndex, isPersonalInfoStage, showIntro, storageKey]);

  const handleChange = (qid, value, index) => {
    setResponses(prev => ({ ...prev, [`${currentSection}-${qid}`]: parseInt(value) }));
    if (index === visibleIndex && index < currentQuestions.length - 1) {
      setTimeout(() => setVisibleIndex(prev => prev + 1), 100);
    }
  };

  const handleNext = () => {
    if (currentSectionIndex < sectionTitles.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);

      // Show questions up to the last answered one plus the next unanswered
      const nextSection = sectionTitles[currentSectionIndex + 1];
      const nextQuestions = selectedQuestions.filter(q => q.section === nextSection);
      
      // Find the index of the last answered question
      let lastAnsweredIndex = -1;
      for (let i = nextQuestions.length - 1; i >= 0; i--) {
        if (responses[`${nextSection}-${nextQuestions[i].id}`] !== undefined) {
          lastAnsweredIndex = i;
          break;
        }
      }
      
      // Set visible index to show all answered questions plus the next unanswered one
      // If all are answered, show the last one; if none answered, show first; otherwise show next unanswered
      const allAnswered = lastAnsweredIndex === nextQuestions.length - 1;
      setVisibleIndex(allAnswered ? lastAnsweredIndex : lastAnsweredIndex + 1);
    } else {
      saveToDatabase(responses, personalInfo, version, partner);
      setIsSubmitted(true);
      localStorage.removeItem(storageKey); // Clear progress on submit
    }
    setTimeout(scrollToTop, 0);
  };

  const handleBack = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
      
      // Show questions up to the last answered one plus the next unanswered
      const prevSection = sectionTitles[currentSectionIndex - 1];
      const prevQuestions = selectedQuestions.filter(q => q.section === prevSection);
      
      // Find the index of the last answered question
      let lastAnsweredIndex = -1;
      for (let i = prevQuestions.length - 1; i >= 0; i--) {
        if (responses[`${prevSection}-${prevQuestions[i].id}`] !== undefined) {
          lastAnsweredIndex = i;
          break;
        }
      }
      
      // Set visible index to show all answered questions plus the next unanswered one
      // If all are answered, show the last one; if none answered, show first; otherwise show next unanswered
      const allAnswered = lastAnsweredIndex === prevQuestions.length - 1;
      setVisibleIndex(allAnswered ? lastAnsweredIndex : lastAnsweredIndex + 1);
    } else if (currentSectionIndex === 0) {
      // Go back to personal info stage from first section
      setIsPersonalInfoStage(true);
    }
    setTimeout(scrollToTop, 0);
  };

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
  };

  const validateAndProceed = () => {
    const { name, company, jobTitle, jobTitleOther, email, fundingStage, fundingStageOther } = personalInfo;
    const isEmailValid = /\S+@\S+\.\S+/.test(email);
    const baseFilled = [name, company, jobTitle, email].every(val => val.trim());
    const founderFilled = version !== 'founder' || (fundingStage && fundingStage.trim());
    const otherFilled = jobTitle !== 'Other' || jobTitleOther.trim();
    const fundingOtherFilled = fundingStage !== 'Other' || fundingStageOther.trim();

    if (!baseFilled) {
      setError('Please fill in all fields.');
    } else if (version === 'founder' && !founderFilled) {
      setError('Please select an investment stage.');
    } else if (fundingStage === 'Other' && !fundingStageOther.trim()) {
      setError('Please specify your investment stage.');
    } else if (jobTitle === 'Other' && !jobTitleOther.trim()) {
      setError('Please specify your job title.');
    } else if (!isEmailValid) {
      setError('Please enter a valid email address.');
    } else {
      setError('');
      // Set cofounder questions flag based on job title
      if (version === 'founder' && (jobTitle === 'Co-Founder' || jobTitle === 'Founder (With a Co-Founder)')) {
        setShowCoFounderQuestions(true);
      }
      setIsPersonalInfoStage(false);
    }
  };

  const devFillAll = () => {
    const filled = {};
    selectedQuestions.forEach(q => {
      filled[`${q.section}-${q.id}`] = Math.floor(Math.random() * 5) + 1;
    });
    setResponses(filled);
    setCurrentSectionIndex(sectionTitles.length - 1);
    setVisibleIndex(
      selectedQuestions.filter(q => q.section === sectionTitles.at(-1)).length - 1
    );
  };

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  };

  const isMobile = useIsMobile();

  // ============================
  // RENDER LOGIC
  // ============================
  if (isSubmitted) {
    return (
      <div className="survey-wrapper">

        <div className="survey-container thank-you-message">
          {partner === 'HotTopics' ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2rem', flexWrap: 'wrap', margin: '0 auto 1rem auto', paddingTop: '1.5rem', paddingBottom: '2rem' }}>
              <img src={require('./HT_logo.png')} alt="HotTopics Logo" style={{ maxWidth: '150px', height: 'auto' }} />
              <img src={require('./RP_Logo_Wordmark_Black.png')} alt="RP Logo" style={{ maxWidth: '150px', height: 'auto' }} />
            </div>
          ) : (
            <img
              id="intro-logo"
              src={require('./RP_Logo_Wordmark_Black.png')}
              alt="RP Logo"
              style={{ maxWidth: '220px', margin: '0 auto 1rem auto', display: 'block', paddingTop: '1.5rem', paddingBottom: '2rem' }}
            />
          )}
          <h2 className="thank-you-heading">Thank you. You've taken a bold first step!</h2>
          <p>We've received your answers, and we want to thank you for pausing, reflecting, and engaging with the {version === 'founder' && (personalInfo.jobTitle === 'Co-Founder' || personalInfo.jobTitle === 'Founder (With a Co-Founder)') ? '8' : '7'} relationships that shape your energy, mindset, and leadership joy.</p>
          <p>This is more than a survey. It's a moment of self-awareness. A courageous step toward understanding the relationship dynamics that impact how you show up at work, in life, and for yourself.</p>
          <p>We'll be sharing your personal report very soon. Our goal is to reach 100 responses. Once we hit that milestone, you'll also receive a benchmark report with insights from {version === 'founder' ? 'other founders' : 'marketing leaders in similar roles'}, showing which areas you're strongest in, where others in your position tend to struggle, and what your data says about how you're experiencing growth.</p>
          <p>We hope it gives you fresh perspective, fuels your intent, and nudges you one step closer to becoming the leader you're meant to be.</p>
          <p>Don't let the grind win.<br /><strong className="thank-you-bold">Choose growth. Choose joy.</strong></p>
          <p>For more information visit <a href="https://www.roosterpunk.com" target="_blank" rel="noopener noreferrer" className="thank-you-link">www.roosterpunk.com</a></p>

        </div>
      </div>
    );
  }

  if (showIntro) {
    return (
      <div className="survey-wrapper">
        <div className="survey-container intro-scrollable" ref={containerRef}>
          {partner === 'HotTopics' ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '2rem', flexWrap: 'wrap', margin: '0 auto 2rem auto', paddingTop: '1.5rem', paddingBottom: '2rem' }}>
              <img src={require('./HT_logo.png')} alt="HotTopics Logo" style={{ maxWidth: '150px', height: 'auto' }} />
              <img src={require('./RP_Logo_Wordmark_Black.png')} alt="RP Logo" style={{ maxWidth: '150px', height: 'auto' }} />
            </div>
          ) : (
            <img
              id="intro-logo"
              src={require('./RP_Logo_Wordmark_Black.png')}
              alt="RP Logo"
              style={{ maxWidth: '220px', margin: '0 auto 2rem auto', display: 'block', paddingTop: '1.5rem', paddingBottom: '2rem' }}
            />
          )}
          {isMobile && (
            <img
              src={HumanizingAuditMobileImage}
              alt="Humanizing Audit Mobile"
              style={{ width: '100%', margin: '0 auto 1.5rem auto', display: 'block', borderRadius: '12px' }}
            />
          )}
          {partner === 'HotTopics' ? (
            <>
              <h1>Welcome to the Joy of growth self-diagnostic tool for {version === 'founder' ? 'founders' : 'marketing leaders'}</h1>
              <p>HotTopics has partnered with Rooster Punk to deliver this Marketing Benchmark survey, designed to uncover insights into the happiness and well-being of marketing leaders today.</p>
              <p>By completing this questionnaire, you'll help shape a deeper understanding of the challenges and motivations driving modern marketing leadership. Your responses will also complement the findings from the HotTopics Marketing Benchmark, giving our community a richer, more holistic view of the industry landscape.</p>
              <p>All responses are completely anonymous, and participants will receive exclusive access to the combined results once the benchmarks are complete.</p>
              <p>Thank you for contributing to this important initiative.</p>
            </>
          ) : version === 'founder' ? (
            <>
              <h1>Welcome to the Joy of growth self-diagnostic tool for founders</h1>
              <h3>What is the Joy of growth?</h3>
              <p>A new way to measure the human side of leadership and growth.</p>
              <p>This self-assessment takes around 5 minutes. It's designed to help you reflect on where you're thriving and where the grind might be creeping in.</p>
              <p>You'll answer 49 questions across seven core relationship areas (56 questions across eight core relationships if you have a Co-founder) that shape your everyday experience as a leader.</p>
              <h3>Why it matters?</h3>
              <p>The Joy of growth isn't a performance review. It is a mirror, offering space to reflect on how your personal growth feels right now. Where things are aligned, where pressure is building and where a reset might be needed.</p>
              <h3>What happens next?</h3>
              <p>Once completed, we'll benchmark your responses against other founders. You'll receive a personalised Joy of growth report in a few weeks, showing which areas you're strongest in, where others in your position tend to struggle, and what your data says about how you're experiencing growth.</p>
            </>
          ) : (
            <>
              <h1>Welcome to the Joy of growth self-diagnostic tool for marketing leaders</h1>
              <h3>What is the Joy of growth?</h3>
              <p>A new way to measure the human side of leadership and growth.</p>
              <p>This self-assessment takes around 5 minutes. It's designed to help you reflect on where you're thriving and where the grind might be creeping in.</p>
              <p>You'll answer 49 questions across seven core relationship areas that shape your everyday experience as a leader.</p>
              <h3>Why it matters?</h3>
              <p>The Joy of growth isn't a performance review. It is a mirror, offering space to reflect on how your personal growth feels right now. Where things are aligned, where pressure is building and where a reset might be needed.</p>
              <h3>What happens next?</h3>
              <p>Once completed, we'll benchmark your responses against other marketing leaders in similar roles. You'll receive a personalised Joy of growth report in a few weeks, showing which areas you're strongest in, where others in your position tend to struggle, and what your data says about how you're experiencing growth.</p>
            </>
          )}
          <h3>Your data and privacy</h3>
          <p>All benchmarking is fully anonymised. We do collect your personal details so we can share your custom report with you directly but your specific results will never be shared with anyone else.</p>
          {partner === 'HotTopics' ? (
          <p>By submitting this form, you agree that Rooster Punk Group Ltd may store and process your personal data to respond to your request and send you relevant communications about our services, and content. You can unsubscribe at any time. For more information on how we use and protect your data, please see our Privacy Policy.</p>
          ) : (
            <></>
          )}
          <button
            onClick={() => {
              setShowIntro(false);
              setTimeout(scrollToTop, 0);
            }}
            style={{marginTop: '1rem'}}
          >
            Begin
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-wrapper">
      <div className="survey-container" ref={containerRef}>
        <h1>The Joy of growth self-diagnostic tool for {version === 'founder' ? 'founders' : 'marketing leaders'}</h1>

        {process.env.NODE_ENV === 'development' && (
          <button onClick={devFillAll} style={{ marginBottom: '1rem' }}>
            🚀 Dev: Auto-fill survey
          </button>
        )}

        {isPersonalInfoStage ? (
          <div className="personal-info-form">
            <h2>Please fill in your details</h2>

            {['name', 'company'].map((field, idx, arr) => (
              <div key={field} className="personal-input-group">
                <label htmlFor={field}>
                  {field === 'email' ? 'Work email' : field.charAt(0).toUpperCase() + field.slice(1)}
                </label>
                <input
                  type={field === 'email' ? 'email' : 'text'}
                  id={field}
                  value={personalInfo[field]}
                  onChange={e => handlePersonalInfoChange(field, e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const nextField = arr[idx + 1];
                      if (nextField) {
                        const nextInput = document.getElementById(nextField);
                        if (nextInput) nextInput.focus();
                      } else {
                        // If last field, move focus to next appropriate field
                        const nextElement = version === 'founder' ? document.getElementById('fundingStage') : document.getElementById('email');
                        if (nextElement) nextElement.focus();
                      }
                    }
                  }}
                  required
                />
              </div>
            ))}

            {version === 'founder' && (
              <>
                <div className="personal-input-group">
                  <label htmlFor="fundingStage">Investment stage</label>
                  <select
                    id="fundingStage"
                    value={personalInfo.fundingStage}
                    onChange={e => handlePersonalInfoChange('fundingStage', e.target.value)}
                    required
                  >
                    <option value="">Select Investment Stage</option>
                    <option value="Pre-seed">Pre-seed</option>
                    <option value="Seed">Seed</option>
                    <option value="Series A">Series A</option>
                    <option value="Series B">Series B</option>
                    <option value="Series C">Series C</option>
                    <option value="Other">Other (please specify)</option>
                  </select>
                </div>

                {personalInfo.fundingStage === 'Other' && (
                  <div className="personal-input-group">
                    <label htmlFor="fundingStageOther">Please specify your investment stage</label>
                    <input
                      type="text"
                      id="fundingStageOther"
                      value={personalInfo.fundingStageOther}
                      onChange={e => handlePersonalInfoChange('fundingStageOther', e.target.value)}
                      required
                    />
                  </div>
                )}
              </>
            )}

            <div className="personal-input-group">
              <label htmlFor="email">Work email</label>
              <input
                type="email"
                id="email"
                value={personalInfo.email}
                onChange={e => handlePersonalInfoChange('email', e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const jobTitleSelect = document.getElementById('jobTitle');
                    if (jobTitleSelect) jobTitleSelect.focus();
                  }
                }}
                required
              />
            </div>

            <div className="personal-input-group">
              <label htmlFor="jobTitle">Job Title</label>
              <select
                id="jobTitle"
                value={personalInfo.jobTitle}
                onChange={e => handlePersonalInfoChange('jobTitle', e.target.value)}
                required
              >
                <option value="">Select Job Title</option>
                {version === 'marketing' ? (
                  <>
                    <option value="CMO">CMO</option>
                    <option value="Marketing Director">Marketing Director</option>
                    <option value="Head of Marketing">Head of Marketing</option>
                    <option value="Marketing Lead">Marketing Lead</option>
                    <option value="Other">Other (please specify)</option>
                  </>
                ) : version === 'founder' ? (
                  <>
                    <option value="Solo Founder">Solo Founder</option>
                    <option value="Founder (With a Co-Founder)">Founder (With a Co-Founder)</option>
                    <option value="Co-Founder">Co-Founder</option>
                  </>
                ) : (
                  <>
                    <option value="CMO">CMO</option>
                    <option value="Founder">Founder</option>
                  </>
                )}
              </select>
            </div>

            {personalInfo.jobTitle === 'Other' && (
              <div className="personal-input-group">
                <label htmlFor="jobTitleOther">Please specify your job title</label>
                <input
                  type="text"
                  id="jobTitleOther"
                  value={personalInfo.jobTitleOther}
                  onChange={e => handlePersonalInfoChange('jobTitleOther', e.target.value)}
                  required
                />
              </div>
            )}

            {error && <p className="error-text">{error}</p>}
            <button onClick={() => { validateAndProceed(); setTimeout(scrollToTop, 0); }}>
              Start Survey
            </button>
          </div>
        ) : (
          <>
            <h2>{currentSectionIndex + 1}: {currentSection}</h2>
              {sectionDescription && <p className="section-description">{sectionDescription}</p>}

              <p className="scale-instructions">Rank 1–5 (1 = Strongly Disagree, 5 = Strongly Agree)</p>


            {currentQuestions.map((q, index) => (
              <div
                key={`${currentSection}-${q.id}`}
                ref={el => (questionRefs.current[index] = el)}
                className={index <= visibleIndex ? 'question-visible' : 'question-hidden'}
              >
                <div className="survey-question">{q.text}</div>
                <div className="radio-group">
                  {[1, 2, 3, 4, 5].map(val => (
                    <React.Fragment key={`${q.id}-${val}`}>
                      <input
                        type="radio"
                        id={`${currentSection}-${q.id}-${val}`}
                        name={`${currentSection}-${q.id}`}
                        value={val}
                        checked={responses[`${currentSection}-${q.id}`] === val}
                        onChange={() => handleChange(q.id, val, index)}
                      />
                      <label htmlFor={`${currentSection}-${q.id}-${val}`}>{val}</label>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}

            {/* Progress bar and Page X of Y - always visible */}
            <div className="survey-progress-label" style={{textAlign: 'center', marginBottom: '0.2rem', marginTop: '2rem'}}>
              Page {currentSectionIndex + 1} / {sectionTitles.length}
            </div>
            <div className="survey-progress-bar-outerbox">
              <div className="survey-progress-bar-wrapper">
                <div className="survey-progress-bar-bg">
                  <div
                    className="survey-progress-bar-fill"
                    style={{ width: `${Math.round(((currentSectionIndex + 1) / sectionTitles.length) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
            
            {/* Navigation buttons - always show since back can go to personal info */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem' }}>
              <button
                onClick={handleBack}
                style={{ marginLeft: 0, marginRight: 'auto' }}
              >
                ← Back
              </button>
              {allCurrentAnswered && (
                <button onClick={handleNext} ref={nextButtonRef} style={{ marginLeft: 'auto', marginRight: 0 }}>
                  {currentSectionIndex < sectionTitles.length - 1 ? 'Next Section' : 'Submit Survey'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SurveyForm;
